import {
  AdminInput,
  Button,
  Disclaimer,
  Div,
  ErrorToast,
  lightTheme,
  P,
} from '@vgrubs/components';
import { ReactNode, useState } from 'react';

const MIN_LENGTH = 2;
const MAX_LENGTH = 2000;

type Props = {
  busy: boolean;
  /** An open ticket: sending a reply claims it, and Resolve is not offered. */
  claimsOnReply: boolean;
  canResolve: boolean;
  /** Claim on an open ticket, first in the row of buttons. */
  leading?: ReactNode;
  onReply: (message: string) => Promise<boolean>;
  onResolve: (resolution: string) => Promise<boolean>;
};

/**
 * Writing to the merchant from the ticket, the same text for both buttons.
 *
 * Reply keeps the ticket open (a question, an update), Resolve closes it.
 * Until Reply existed Resolve was the only way to answer, so a merchant asked
 * for a screenshot saw the ticket marked Resolved - which is why both sit
 * under one field, and the line above them says which does what.
 *
 * The text is never an internal note: the backend posts it into their thread
 * as a reply to the message that opened the ticket, so it is written to them.
 * Mounted per ticket (keyed by id), so a draft never follows you to another.
 */
export const TicketReplyComposer = ({
  busy,
  claimsOnReply,
  canResolve,
  leading,
  onReply,
  onResolve,
}: Props) => {
  const [text, setText] = useState('');
  // Which button the request in flight belongs to, so only it spins.
  const [sending, setSending] = useState<'reply' | 'resolve' | null>(null);

  const trimmed = text.trim();
  /* What is wrong with the text, if anything. Said in a toast when a button
     is pressed anyway, so a grey button explains itself rather than looking
     broken - which is also why it is aria-disabled, not disabled: a disabled
     button never receives the click. */
  const textProblem =
    trimmed.length === 0
      ? 'Write a message to the merchant first.'
      : trimmed.length < MIN_LENGTH
      ? `The message needs at least ${MIN_LENGTH} characters.`
      : null;

  const send = async (action: 'reply' | 'resolve') => {
    if (busy) return;
    if (textProblem) {
      ErrorToast(textProblem);
      return;
    }
    setSending(action);
    const ok = await (action === 'reply' ? onReply : onResolve)(trimmed);
    setSending(null);
    if (ok) setText('');
  };

  return (
    <>
      <P $m="0 0 4px 0">Write to the merchant</P>
      <Disclaimer $m="0 0 12px 0" $color={lightTheme.lightGray}>
        {claimsOnReply
          ? 'Goes to the merchant as a reply to their message. Replying claims the ticket for you and keeps it open.'
          : 'Goes to the merchant as a reply to their message. Reply keeps the ticket open, Resolve closes it.'}
      </Disclaimer>

      {/* maxLength draws its own counter under the field, which is also why
          the explanation sits above it. */}
      <AdminInput
        type="textarea"
        placeholder="A question, an update, or what was done"
        value={text}
        maxLength={MAX_LENGTH}
        $textareaHeight={120}
        onChange={(event) => setText(event.target.value)}
      />

      <Div $flex $gap="12px" $mt="24">
        {leading}

        <Button
          $w="100%"
          $wSM="100%"
          $loading={sending === 'reply'}
          /* An outlined button: the default white spinner would vanish on the
             cream it sits on. */
          $loadingColor={lightTheme.primary}
          // Only the OTHER buttons grey out; this one shows its spinner.
          disabled={busy && sending !== 'reply'}
          aria-disabled={!!textProblem}
          onClick={() => send('reply')}
        >
          {/* Says what pressing it does to an open ticket, as the line above
              the field does. */}
          {claimsOnReply ? 'Reply & Claim' : 'Reply'}
        </Button>

        {canResolve && (
          <Button
            $selected
            $w="100%"
            $wSM="100%"
            $loading={sending === 'resolve'}
            disabled={busy && sending !== 'resolve'}
            aria-disabled={!!textProblem}
            onClick={() => send('resolve')}
          >
            Resolve
          </Button>
        )}
      </Div>
    </>
  );
};
